/**
 * Password validation utilities
 * Validates password strength requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */

const PASSWORD_REQUIREMENTS = [
  { regex: /.{8,}/, message: 'At least 8 characters' },
  { regex: /[A-Z]/, message: 'At least one uppercase letter' },
  { regex: /[a-z]/, message: 'At least one lowercase letter' },
  { regex: /[0-9]/, message: 'At least one number' },
  { regex: /[!@#$%^&*(),.?":{}|<>\-_=+[\]\\;'/~]/, message: 'At least one special character' },
];

/**
 * Validates a password against all requirements
 * @param {string} password - The password to validate
 * @returns {{ isValid: boolean, errors: string[] }} Validation result
 */
export const validatePassword = (password) => {
  const errors = [];

  PASSWORD_REQUIREMENTS.forEach(({ regex, message }) => {
    if (!regex.test(password)) {
      errors.push(message);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Returns the list of password requirements for display
 * @returns {string[]} Array of requirement descriptions
 */
export const getPasswordRequirements = () => {
  return PASSWORD_REQUIREMENTS.map(req => req.message);
};

/**
 * Checks if a password is valid (simple boolean check)
 * @param {string} password - The password to validate
 * @returns {boolean} True if password meets all requirements
 */
export const isPasswordValid = (password) => {
  return validatePassword(password).isValid;
};

/**
 * Gets a formatted error message for invalid passwords
 * @param {string} password - The password to validate
 * @returns {string} Error message or empty string if valid
 */
export const getPasswordErrorMessage = (password) => {
  const { errors } = validatePassword(password);
  if (errors.length === 0) return '';
  return `Password must have: ${errors.join(', ')}`;
};
